import React from 'react';
import { useTranslation } from 'react-i18next';
import RaceBar from 'lib/RaceBar/RaceBar';
import DeltaBubble from 'lib/DeltaBubble/DeltaBubble';
import getSpecialCaseParty from './getSpecialCaseParty';
import { PartyBarData } from './partybar.types';

export type PartyBarType = 'seats' | 'votes';

interface Props {
  list: PartyBarData;
  maxValue: number;
  currentElectionYear: number;
  prevElectionYear: number;
  isFinal: boolean;
  type: PartyBarType;
  isMinimal?: boolean;
  // true when at least one of the other list has a previous result
  hasAnyListPrevValues?: boolean;
  enforceDecimals?: boolean;
}

const PartyBar = ({
  list,
  maxValue,
  currentElectionYear,
  prevElectionYear,
  isFinal,
  type,
  isMinimal,
  hasAnyListPrevValues,
  enforceDecimals,
}: Props) => {
  const { t } = useTranslation('wap');
  let resultString: string;
  let resultStringPrev: string;
  let valueThisElection: number;
  let valueLastElection: number;
  let translationStringBubble: string;
  let placeholderWidth: string;

  const formattedVotesAbsolute = enforceDecimals
    ? t('formatted_number_fixed', {
        count: list.votesAbsolute,
      })
    : t('formatted_number', {
        count: list.votesAbsolute,
      });

  switch (type) {
    case 'seats':
      resultString = t('seats', { count: list.seatsClaimed || 0 });
      resultStringPrev = t('seats', { count: list.seatsClaimedPrev || 0 });
      valueThisElection = list.seatsClaimed;
      valueLastElection = list.seatsClaimedPrev;
      translationStringBubble = 'seats';
      // enough to fit a two digit number of seats and the word "Sitze"
      placeholderWidth = '7ch';
      break;
    case 'votes':
      resultString = list.noCirclesCounted
        ? t('partybar_no_circles_counted')
        : `${formattedVotesAbsolute} / ${t('percentage', {
            count: list.votesRelative,
          })}`;
      resultStringPrev = t('percentage', { count: list.votesRelativePrev });
      valueThisElection = list.votesRelative;
      valueLastElection = list.votesRelativePrev;
      translationStringBubble = 'percentage';
      // enough to fit a nine digit votes result (9.999'999.99) plus 4 digit percentage number
      placeholderWidth = '14ch';
      break;
    default:
      break;
  }

  const delta = parseFloat((valueThisElection - (valueLastElection || 0)).toFixed(2));

  let party: React.ReactNode;

  // if long title includes party abbreviation (short title), we print the short title bold
  if (list.title.includes(list.shortTitle)) {
    const indexOfShortTitle = list.title.indexOf(list.shortTitle);
    const lengthOfShortTitle = list.shortTitle.length;
    const indexOfShortTitleEnd = indexOfShortTitle + lengthOfShortTitle;
    const firstPart = list.title.slice(0, indexOfShortTitle); // usually empty string since short title is at the beginning
    const secondPart = list.title.slice(indexOfShortTitleEnd);

    party = (
      <div className="partybar__party">
        {firstPart}
        <strong>{list.shortTitle}</strong>
        {secondPart}
      </div>
    );
  } else {
    party = getSpecialCaseParty(list);
  }

  const hasResultPrev = Boolean(valueLastElection || valueLastElection === 0);
  const hidePrevBar = !hasResultPrev && !hasAnyListPrevValues; // if no previous result and no other list has a previous result, we hide the previous bar
  const showNotAttendedNoticePrevElection = !hasResultPrev && hasAnyListPrevValues; // if some of the other lists have a previous result, we show a notice that this list did not attend
  const showNotAttendedNoticeCurrentElection = list.competedInCurrentElection === false;
  const showDelta = !list.noCirclesCounted;

  if (isMinimal || hidePrevBar) {
    return (
      <div className="partybar">
        {party}
        <div className="partybar__content">
          <div className="partybar__bars">
            <RaceBar
              maxValue={maxValue}
              value={valueThisElection}
              resultString={resultString}
              isFinal={isFinal}
            />
          </div>
          {/* this transparent element ensures, the absolute positioned result string has always enough space on right side of race bar */}
          <div className="partybar__placeholder" style={{ width: placeholderWidth }} />
        </div>
      </div>
    );
  }

  return (
    <div className="partybar">
      {party}
      <div className="partybar__content">
        <div className="partybar__bars">
          {!showNotAttendedNoticeCurrentElection ? (
            <RaceBar
              maxValue={maxValue}
              value={valueThisElection}
              resultString={resultString}
              screenReaderAppendix={`(${currentElectionYear})`}
              isFinal={isFinal}
            />
          ) : (
            <div className="partybar__not-competed">
              {t('did_not_compete', { year: currentElectionYear })}
            </div>
          )}
          {!showNotAttendedNoticePrevElection ? (
            <RaceBar
              small
              maxValue={maxValue}
              value={valueLastElection}
              resultString={resultStringPrev}
              screenReaderAppendix={`(${prevElectionYear})`}
              isFinal={isFinal}
            />
          ) : (
            <div className="partybar__not-competed">
              {t('did_not_compete', { year: prevElectionYear })}
            </div>
          )}
        </div>
        {/* this transparent element ensures, the absolute positioned result string has always enough space on right side of race bar */}
        <div className="partybar__placeholder" style={{ width: placeholderWidth }} />
        {showDelta && (
          <DeltaBubble delta={delta} translationString={translationStringBubble} />
        )}
      </div>
    </div>
  );
};

export default PartyBar;
